import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import tableIllustration from "resources/images/table-illustration.png";
import { MediumCustomIcon, SmallCustomIcon } from "../../layer00";
import { EmptyStateCard } from "../../layer01";
import { UpcomingOneOnOneCard } from "../../layer20";
import { useState } from "react";
import { useCalendarDrawer } from "../../../common";
import { useGetDetectedMeetingsQuery } from "app/containers/TeamsTab/calendarApi";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectCurrentUserInfo,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";

export const UpcomingOneOnOneList = () => {
  const calendarDrawer = useCalendarDrawer();
  const [open, setOpen] = useState(true);
  const theme = useTheme();
  const { data, isLoading } = useGetDetectedMeetingsQuery();
  const currentUser = useAppSelector(selectCurrentUserInfo);
  const currentUserEmail = currentUser?.emailAddress;
  const companyUsersById = useAppSelector(selectAllCompanyUsersById);
  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const userAccountIdByEmail = Object.fromEntries(
    Object.entries({ ...sampleUsersById, ...companyUsersById }).map(
      ([id, user]) => [user.emailAddress, Number(id)]
    )
  );
  const meetings = (data?.detected_1on1s ?? []).filter(
    (meeting) =>
      meeting.participants.length === 2 &&
      DateTime.fromISO(meeting.startTime) > DateTime.now()
  );
  return (
    <Card
      className="bg-white"
      sx={{
        paddingLeft: "2rem",
        paddingRight: "2rem",
      }}
    >
      <CardHeader
        disableTypography
        className={open ? "border-b border-[#8080801f]" : ""}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          paddingLeft: 0,
          paddingRight: 0,
          cursor: "pointer",
        }}
        title={
          <div>
            <Typography variant="cardTitle">Upcoming 1:1s</Typography>
            <Button
              startIcon={<SmallCustomIcon icon="CALENDAR" />}
              variant="outlined"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation();
                calendarDrawer.open();
              }}
              className="px-3 py-1 ms-2"
            >
              View 1:1 calendar
            </Button>
          </div>
        }
        subheader=""
        action={
          <IconButton
            sx={{
              border: "none",
            }}
          >
            <MediumCustomIcon
              icon="CHEVRONUP"
              stroke={theme.palette.secondary.light}
              rotated={!open}
            />
          </IconButton>
        }
      />
      <Collapse in={open}>
        <CardContent
          sx={{
            background: "none",
            padding: 0,
            paddingBottom: "1rem",
          }}
        >
          <Stack
            paddingBottom="0rem"
            marginBottom="1rem"
            marginTop="1rem"
            paddingLeft="1rem"
            rowGap="1rem"
          >
            {isLoading && (
              <>
                <Skeleton
                  variant="rounded"
                  height={88}
                  sx={{ borderRadius: "1rem" }}
                />
                <Skeleton
                  variant="rounded"
                  height={88}
                  sx={{ borderRadius: "1rem" }}
                />
              </>
            )}
            {!isLoading && meetings.length === 0 && (
              <EmptyStateCard
                image={tableIllustration}
                title="No upcoming 1:1s"
                body="You don't have any one on ones scheduled."
              />
            )}
            {!isLoading &&
              meetings.map((meeting) => {
                const otherParticipant =
                  meeting.participants.find(
                    (participant) => participant.email !== currentUserEmail
                  ) ?? meeting.participants[0];
                const personName =
                  otherParticipant?.name ||
                  otherParticipant?.email ||
                  meeting.title;
                const userAccountId = otherParticipant
                  ? userAccountIdByEmail[otherParticipant.email] ?? null
                  : null;

                return (
                  <UpcomingOneOnOneCard
                    key={meeting.id}
                    personName={personName}
                    meetingTime={DateTime.fromISO(meeting.startTime)}
                    userAccountId={userAccountId}
                  />
                );
              })}
          </Stack>
          <Box width="100%">
            <Button
              startIcon={<SmallCustomIcon icon="CALENDAR" />}
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => calendarDrawer.open()}
            >
              View 1:1 calendar
            </Button>
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};
